import {
  base_colorAction,
  base_colorContentBorder,
  base_colorContentBorderSecondary,
  base_colorRaisedSurface,
  tag_colorTagBlueBackground,
  tag_colorTagSevereBackground,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_2xl,
  font_4xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_lg,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

import { flyoutSurface } from '../billPayFlyoutChrome.style'

export default css`
  ${flyoutSurface('PaymentDetailFlyout')}

  &.PaymentDetailFlyout .BaseFlyout-Content {
    width: min(${spacing(160)}, 100vw);

    .BaseFlyout-CloseBar {
      margin-bottom: 0;
      padding: ${spacing(6)} ${spacing(10)} 0;
    }
  }

  .BaseFlyout-Body {
    display: flex;
    min-height: 0;
    padding: 0;
  }

  .payment-detail-layout {
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
  }

  .payment-detail-header,
  .payment-detail-tabs,
  .payment-detail-scroll {
    padding-right: ${spacing(10)};
    padding-left: ${spacing(10)};
  }

  /* EX-40842 (Richa): "no line above tabs". The tab bar sits directly beneath
     this header and carries its own rule along its bottom edge, so a divider
     here stacked a second line immediately above the tab labels. */
  .payment-detail-header {
    padding-top: ${spacing(6)};
    /* Half of the gap to the tab bar; the tab bar's own padding-top is the
       other half. 24 + 24 left 48px of nothing between the subtitle and the tab
       labels, which read as a break in the flyout rather than a header sitting
       on its tabs (EX-40842). */
    padding-bottom: ${spacing(4)};

    h2 {
      margin: 0 0 ${spacing(1)};
      color: ${text_colorTextPrimary};
      ${font_4xl};
      font-family: ${font_family_weight_medium};
      font-weight: 500;
    }

    p {
      margin: 0;
      color: ${text_colorTextSecondary};
    }
  }

  .payment-detail-subtitle {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${spacing(2)};
    ${font_md};
    color: ${text_colorTextSecondary};
  }

  /* Screen-reader-only live region for status changes (see the Pill in the
     detail header, which carries no live-region semantics). Same clip
     technique as AddVendorFlyout's visuallyHidden. */
  .payment-status-announcement {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .payment-detail-alert {
    padding: ${spacing(6)} ${spacing(10)} 0;

    .AlertPanel .subtitle {
      display: flex;
      flex-direction: column;
      gap: ${spacing(1)};
    }
  }

  .payment-detail-tabs {
    display: flex;
    gap: ${spacing(8)};
    /* Matches the header's padding-bottom — see the note there. */
    padding-top: ${spacing(4)};
    border-bottom: 1px solid ${base_colorContentBorder};

    button {
      padding: 0 0 ${spacing(3)};
      border: 0;
      border-bottom: 2px solid transparent;
      background: transparent;
      ${font_md};
      font-family: ${font_family_weight_medium};
      color: ${text_colorTextSecondary};
      cursor: pointer;
    }

    button[aria-selected='true'] {
      border-bottom-color: ${base_colorAction};
      color: ${base_colorAction};
    }

    button:focus-visible {
      border-radius: ${spacing(1)};
      outline: 2px solid ${base_colorAction};
      outline-offset: ${spacing(1)};
    }
  }

  .payment-detail-scroll {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    flex-direction: column;

    section {
      padding: ${spacing(6)} 0;
    }
    section + section {
      border-top: 1px solid ${base_colorContentBorder};
    }
    h3 {
      margin: 0 0 ${spacing(5)};
      ${font_2xl};
      font-family: ${font_family_weight_medium};
      font-weight: 500;
      color: ${text_colorTextPrimary};
    }
    p {
      margin: 0;
      white-space: pre-wrap;
      color: ${text_colorTextPrimary};
    }
  }

  dl {
    margin: 0;
  }
  dl > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing(4)};
    padding: ${spacing(3)} 0;
  }
  dl > div + div {
    border-top: 1px solid ${base_colorContentBorderSecondary};
  }
  dt {
    flex-shrink: 0;
    ${font_md};
    color: ${text_colorTextSecondary};
  }
  dd {
    min-width: 0;
    margin: 0;
    text-align: right;
    ${font_lg};
    color: ${text_colorTextPrimary};
  }

  .payment-detail-link {
    max-width: 100%;
    padding: 0;
    border: 0;
    font: inherit;
    background: transparent;
    color: ${base_colorAction};
    text-decoration: underline;
    overflow-wrap: anywhere;
    cursor: pointer;
  }

  .payment-activity-list {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
    flex-direction: column;
  }
  .payment-activity-list li {
    position: relative;
    display: grid;
    grid-template-columns: ${spacing(5)} 1fr;
    gap: ${spacing(1)} ${spacing(3)};
  }
  .payment-activity-list li:not(:last-child) {
    padding-bottom: ${spacing(6)};
  }
  .payment-activity-list li:not(:last-child)::after {
    position: absolute;
    top: ${spacing(5)};
    bottom: 0;
    left: calc(${spacing(2.5)} - 0.5px);
    width: 1px;
    content: '';
    background: ${base_colorContentBorder};
  }
  .payment-activity-list
    li[data-connector-state='completed']:not(:last-child)::after,
  .payment-activity-list
    li[data-connector-state='error']:not(:last-child)::after {
    background: ${base_colorAction};
  }
  .payment-activity-list strong {
    grid-column: 2;
    ${font_md};
    font-family: ${font_family_weight_regular};
    font-weight: 400;
    color: ${text_colorTextPrimary};
  }
  .payment-activity-meta {
    display: flex;
    grid-column: 2;
    flex-wrap: wrap;
    gap: ${spacing(1)};
    ${font_sm};
    color: ${text_colorTextSecondary};
  }
  .payment-activity-list .payment-activity-reason {
    grid-column: 2;
    font-size: 14px;
    color: ${text_colorTextSecondary};
  }
  .payment-activity-list .payment-activity-marker {
    z-index: 1;
    display: flex;
    grid-column: 1;
    grid-row: 1 / span 2;
    width: ${spacing(5)};
    height: ${spacing(5)};
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${base_colorRaisedSurface};
  }
  .payment-activity-list li[data-state='completed'] .payment-activity-marker {
    background: ${tag_colorTagBlueBackground};

    .CheckmarkIcon {
      width: ${spacing(3)};
      height: ${spacing(3)};
    }
    .CheckmarkIcon path {
      fill: ${base_colorAction};
    }
  }
  .payment-activity-list li[data-state='current'] .payment-activity-marker {
    border: 2px solid ${base_colorAction};
  }
  .payment-activity-current-dot {
    width: ${spacing(2)};
    height: ${spacing(2)};
    border-radius: 50%;
    background: ${base_colorAction};
  }
  .payment-activity-list li[data-state='error'] .payment-activity-marker {
    background: ${tag_colorTagSevereBackground};

    svg {
      width: ${spacing(3)};
      height: ${spacing(3)};
    }
  }

  .payment-detail-footer {
    display: flex;
    justify-content: flex-end;
    gap: ${spacing(3)};
    padding: ${spacing(6)} ${spacing(5)};
    border-top: 1px solid ${base_colorContentBorder};
    background: ${base_colorRaisedSurface};
  }

  .payment-detail-message {
    display: flex;
    margin: auto;
    padding: ${spacing(8)};
    align-items: center;
    flex-direction: column;
    gap: ${spacing(4)};
    color: ${text_colorTextSecondary};
  }
  .payment-detail-message p {
    margin: 0;
  }
`
